import * as HttpStatus from 'http-status-codes';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { first } from 'rxjs/operators';
import { TranslationMWService } from '@services/translation-middleware/translation-middleware.service';
import { ToastrService } from 'ngx-toastr';
import { notEqualToValue } from '@shared/validators/not-equal-to-value.validator';
import { BEActionTokensService } from '@services/backend-services/action-tokens/be-action-tokens.service';
import { IActionTokenRequestContext } from '@interfaces/action-token-request-context.interface';
import { ActionTokenKindsConstants } from '@constants/action-tokens.constants';
import { errorLoginUser } from '@utils/users';

export interface VerifyEmailDialogInjectedData {
  email: string;
}

@Component({
  selector: 'app-verify-email-dialog',
  templateUrl: './verify-email-dialog.component.html',
  styleUrls: ['./verify-email-dialog.component.scss'],
})
export class VerifyEmailDialogComponent {
  public hidePassword = true;
  public actionForm: FormGroup;
  public changeEmailAddress = true;

  private currentDomain: string;

  constructor(
    private toastr: ToastrService,
    private beActionTokensService: BEActionTokensService,
    private translationMWService: TranslationMWService,
    public dialog: MatDialogRef<VerifyEmailDialogComponent>,
    @Inject(Window) private window: Window,
    @Inject(MAT_DIALOG_DATA) public data: VerifyEmailDialogInjectedData
  ) {
    this.currentDomain =
      this.window.location.hostname +
      (this.window.location.port.length ? ':'.concat(this.window.location.port) : '');

    this.actionForm = new FormGroup(
      {
        email: new FormControl(this.data.email, [
          Validators.required,
          Validators.email,
          Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
        ]),
        password: new FormControl('', []),
      },
      { updateOn: 'change' }
    );
    this.toggleChangeEmailAddress();
  }

  deny(): void {
    this.dialog.close(false);
  }

  confirm(): void {
    this.dialog.close(true);
  }

  toggleChangeEmailAddress() {
    this.changeEmailAddress = !this.changeEmailAddress;

    if (this.changeEmailAddress) {
      this.actionForm.controls.password.setValidators([Validators.required]);
      this.actionForm.controls.email.setValidators([
        Validators.required,
        Validators.email,
        Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
        notEqualToValue(this.data.email),
      ]);
      this.actionForm.controls.email.setValue('');
    } else {
      this.actionForm.controls.password.clearValidators();
      this.actionForm.controls.email.clearValidators();
      this.actionForm.controls.email.setValue(this.data.email);
    }

    this.actionForm.controls.email.updateValueAndValidity();
    this.actionForm.controls.password.updateValueAndValidity();
  }

  public requestActionToken() {
    if (this.actionForm.valid) {
      const payload: IActionTokenRequestContext = {
        kind: this.changeEmailAddress
          ? ActionTokenKindsConstants.CHANGE_EMAIL.value
          : ActionTokenKindsConstants.ACTIVATE_ACCOUNT.value,
        domain: this.currentDomain,
        email: this.data.email,
        newEmail: this.actionForm.controls.email.value,
        password: this.actionForm.controls.password.value,
        language: this.translationMWService.currentLanguage,
      };
      this.beActionTokensService
        .request(payload)
        .pipe(first())
        .subscribe(
          () => {
            this.toastr.info(
              this.translationMWService.instant(
                'register.toastr.activate-account-instructions.message',
                {
                  email: this.changeEmailAddress
                    ? this.actionForm.controls.email.value
                    : this.data.email,
                }
              ),
              this.translationMWService.instant(
                'register.toastr.activate-account-instructions.title'
              ),
              {
                disableTimeOut: true,
                closeButton: true,
              }
            );
            this.confirm();
          },
          (error) => {
            if (error.status === HttpStatus.StatusCodes.NOT_FOUND) {
              errorLoginUser(error, this.toastr, this.translationMWService);
            }
          }
        );
    }
  }
}
